/**
 * FE-only minimal renderer for the Mustache-style summary-template syntax,
 * used by the form-builder's summary-template live preview.
 *
 * **Why this duplicates `request-summary-renderer.ts` from the backend
 * service**: the runtime renderer lives in `request-center-service`,
 * which the FE can't import from. Promoting it to `@kfolks/models` (or a
 * neutral package) is a follow-up; for the first cut of the visual
 * builder, a 30-line FE-side reimplementation of the **template path**
 * (placeholder substitution + section blocks) is the right trade-off.
 * The auto-fallback path the backend renderer also implements is NOT
 * needed here — the builder always has a template (it's what the admin
 * is writing).
 *
 * Behaviour deliberately matches the backend renderer:
 *   - `{{token}}` is replaced with `values[token] ?? ''` (tolerant —
 *     unknown tokens render as empty strings).
 *   - `{{#field}}…{{/field}}` keeps the inner content only when
 *     `values[field]` is non-empty.
 *   - Inner content of section blocks can itself contain `{{tokens}}`.
 *   - Nested sections are NOT supported (matches backend; admin will hit
 *     this as a validator error before runtime).
 */

const TOKEN_PATTERN = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g
const SECTION_PATTERN = /\{\{#\s*([a-zA-Z0-9_]+)\s*\}\}([\s\S]*?)\{\{\/\s*\1\s*\}\}/g

export interface RenderSummaryOptions {
	/**
	 * Map of token id → display value. Missing or empty-string entries
	 * cause section blocks referencing the same id to drop their inner
	 * content (matches the runtime renderer's "non-empty" rule).
	 */
	values: Record<string, string>
}

/**
 * Render a summary template against placeholder values. The builder uses
 * deterministic placeholder values (one per known field id) so the live
 * preview is stable across keystrokes.
 */
export function renderSummaryPreview(template: string, options: RenderSummaryOptions): string {
	if (!template) return ''
	const values = options.values

	// Pass 1: section blocks. Replace from the inside out by running
	// `replace` repeatedly until no more matches — handles non-overlapping
	// sequences. Nested sections are caught by the validator at save time;
	// the backend behaves the same way.
	let out = template
	let prev: string
	do {
		prev = out
		out = out.replace(SECTION_PATTERN, (_full, id: string, inner: string) => {
			const v = values[id]
			if (v === undefined || v === '') return ''
			return inner
		})
	} while (out !== prev)

	// Pass 2: leaf tokens.
	out = out.replace(TOKEN_PATTERN, (_full, id: string) => {
		const v = values[id]
		return v ?? ''
	})

	return out
}

/**
 * Build the placeholder values map for a given set of field ids. Each id
 * gets a deterministic-looking placeholder (`<<id>>`) that's obvious at a
 * glance and stable across renders.
 */
export function buildPlaceholderValues(fieldIds: ReadonlyArray<string>): Record<string, string> {
	const out: Record<string, string> = {}
	for (const id of fieldIds) out[id] = `<<${id}>>`
	return out
}
